import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, History } from 'lucide-react';
import sessionService from '@/services/sessionService.ts';
import type { SessionResponse } from '@/types/session.ts';
import type { SimulationResponse } from '@/types/simulation.ts';
import { maestros, getMaestroName } from '@/types/maestro.ts';
import { useCatalog, useCatalogLabelMap } from '@/hooks/useCatalog.ts';
import { formatDate } from '@/utils/formatDate.ts';
import type { SortConfig } from '@/hooks/useSortedSimulations.ts';
import type { GroupByKey } from '@/hooks/useGroupedSimulations.ts';
import { useAsyncData } from '@/hooks/useAsyncData.ts';
import SessionDetail from '@/components/dashboard/SessionDetail.tsx';
import { toScore99 } from '@/utils/scoreFormat.ts';

interface SessionGroup {
  key: string;
  label: string;
  sessions: SessionResponse[];
}

interface PastSessionsListProps {
  simulations: SimulationResponse[];
  sort: SortConfig;
  groupBy: GroupByKey;
}

const COL_COUNT = 6;

export default function PastSessionsList({ simulations, sort, groupBy }: PastSessionsListProps) {
  const { t, i18n } = useTranslation();
  const typeLabels = useCatalogLabelMap('SimulationType');
  const difficultyLabels = useCatalogLabelMap('Difficulty');
  const { data: typeEntries } = useCatalog('SimulationType');
  const { data: sessions = [], loading } = useAsyncData(
    () => sessionService.getMine(1, 50).then((r) => r.items),
    [],
    { initial: [] },
  );
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  const simulationMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const sim of simulations) {
      map.set(sim.id, sim.name);
    }
    return map;
  }, [simulations]);

  // Sort sessions
  const sorted = useMemo(() => {
    if (sort.key === 'default') return sessions;

    const dir = sort.direction === 'asc' ? 1 : -1;
    const copy = [...sessions];

    copy.sort((a, b) => {
      switch (sort.key) {
        case 'name': {
          const aName = (a.simulationId ? simulationMap.get(a.simulationId) : t('pastSessions.quickPracticeLabel')) ?? '';
          const bName = (b.simulationId ? simulationMap.get(b.simulationId) : t('pastSessions.quickPracticeLabel')) ?? '';
          return dir * aName.localeCompare(bName, i18n.language);
        }
        case 'difficultyLevel':
          return dir * (a.difficulty - b.difficulty);
        case 'createdAt': {
          const aDate = a.completedAt ?? a.startedAt;
          const bDate = b.completedAt ?? b.startedAt;
          return dir * (new Date(aDate).getTime() - new Date(bDate).getTime());
        }
        default:
          return 0;
      }
    });
    return copy;
  }, [sessions, sort.key, sort.direction, simulationMap]);

  // Group sessions
  const groups = useMemo((): SessionGroup[] => {
    if (groupBy === 'none') {
      return [{ key: '__all__', label: '', sessions: sorted }];
    }

    const getKeys = (s: SessionResponse): string[] => {
      if (groupBy === 'simulationType') return [s.simulationType];
      if (groupBy === 'difficultyLevel') return [String(s.difficulty)];
      if (groupBy === 'maestro') return [s.maestroId];
      return ['__all__'];
    };

    const getOrderedKeys = (): string[] => {
      if (groupBy === 'simulationType') {
        return [...(typeEntries ?? [])]
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((e) => e.slug);
      }
      if (groupBy === 'difficultyLevel') return ['1', '2', '3', '4', '5'];
      if (groupBy === 'maestro') return maestros.map((m) => m.id);
      return [];
    };

    const getLabel = (key: string): string => {
      if (groupBy === 'simulationType') return typeLabels.get(key) ?? key;
      if (groupBy === 'difficultyLevel') return difficultyLabels.get(key) ?? key;
      if (groupBy === 'maestro') {
        const m = maestros.find((x) => x.id === key);
        return m ? getMaestroName(m) : key;
      }
      return key;
    };

    const buckets = new Map<string, SessionResponse[]>();
    for (const s of sorted) {
      for (const key of getKeys(s)) {
        const list = buckets.get(key);
        if (list) list.push(s);
        else buckets.set(key, [s]);
      }
    }

    const result: SessionGroup[] = [];
    const orderedKeys = getOrderedKeys();

    // Reverse group order when sort matches group criterion and direction is desc
    const reverseOrder = sort.direction === 'desc' && (
      (groupBy === 'difficultyLevel' && sort.key === 'difficultyLevel')
    );
    const keyOrder = reverseOrder ? [...orderedKeys].reverse() : orderedKeys;

    for (const key of keyOrder) {
      const items = buckets.get(key);
      if (items && items.length > 0) {
        result.push({ key, label: getLabel(key), sessions: items });
        buckets.delete(key);
      }
    }
    for (const [key, items] of buckets) {
      if (items.length > 0) {
        result.push({ key, label: getLabel(key), sessions: items });
      }
    }
    return result;
  }, [sorted, groupBy, sort.key, sort.direction, typeLabels, difficultyLabels, typeEntries]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="mt-3 text-sm">{t('pastSessions.loading')}</p>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <History className="h-10 w-10 text-muted-foreground/40" />
        <p className="mt-3 text-sm font-medium text-foreground">
          {t('pastSessions.noSessionsTitle')}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('pastSessions.noSessionsDesc')}
        </p>
      </div>
    );
  }

  if (selectedSessionId) {
    return (
      <SessionDetail
        sessionId={selectedSessionId}
        onBack={() => setSelectedSessionId(null)}
      />
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="whitespace-nowrap pb-2.5 pr-4 font-medium text-muted-foreground">{t('pastSessions.tableDate')}</th>
              <th className="whitespace-nowrap pb-2.5 pr-4 font-medium text-muted-foreground">{t('pastSessions.tableConfig')}</th>
              <th className="whitespace-nowrap pb-2.5 pr-4 font-medium text-muted-foreground">{t('pastSessions.tableType')}</th>
              <th className="whitespace-nowrap pb-2.5 pr-4 font-medium text-muted-foreground">{t('pastSessions.tableMaestro')}</th>
              <th className="whitespace-nowrap pb-2.5 pr-4 font-medium text-muted-foreground">{t('pastSessions.tableDuration')}</th>
              <th className="whitespace-nowrap pb-2.5 font-medium text-muted-foreground">{t('pastSessions.tableScore')}</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <SessionTableGroup
                key={group.key}
                group={group}
                showHeader={groupBy !== 'none'}
                simulationMap={simulationMap}
                onSelect={setSelectedSessionId}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SessionTableGroup({
  group,
  showHeader,
  simulationMap,
  onSelect,
}: {
  group: SessionGroup;
  showHeader: boolean;
  simulationMap: Map<string, string>;
  onSelect: (id: string) => void;
}) {
  return (
    <>
      {showHeader && (
        <tr>
          <td colSpan={COL_COUNT} className="pt-4 pb-2">
            <span className="text-sm font-semibold text-foreground">{group.label}</span>
            <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              {group.sessions.length}
            </span>
          </td>
        </tr>
      )}
      {group.sessions.map((session) => (
        <SessionRow
          key={session.id}
          session={session}
          simulationMap={simulationMap}
          onSelect={onSelect}
        />
      ))}
    </>
  );
}

function SessionRow({
  session,
  simulationMap,
  onSelect,
}: {
  session: SessionResponse;
  simulationMap: Map<string, string>;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();
  const maestro = maestros.find((m) => m.id === session.maestroId);
  const date = session.completedAt
    ? formatDate(session.completedAt, { day: 'numeric', month: 'short', year: 'numeric' })
    : '-';
  const duration = session.durationSeconds
    ? `${Math.round(session.durationSeconds / 60)} min`
    : '-';
  const score = session.report?.overallScore ?? 0;
  const configName = session.simulationId
    ? simulationMap.get(session.simulationId) ?? '-'
    : t('pastSessions.quickPracticeLabel');

  return (
    <tr
      onClick={() => onSelect(session.id)}
      className="cursor-pointer border-b border-border/50 last:border-0 transition-colors hover:bg-muted"
    >
      <td className="whitespace-nowrap py-2.5 pr-4 text-muted-foreground">{date}</td>
      <td className="whitespace-nowrap py-2.5 pr-4 text-foreground font-medium">{configName}</td>
      <td className="whitespace-nowrap py-2.5 pr-4 text-foreground">{session.simulationType}</td>
      <td className="whitespace-nowrap py-2.5 pr-4">
        <span className="font-medium" style={{ color: maestro?.accentColor ?? 'var(--primary)' }}>
          {maestro ? getMaestroName(maestro) : session.maestroId}
        </span>
      </td>
      <td className="whitespace-nowrap py-2.5 pr-4 text-muted-foreground">{duration}</td>
      <td className="py-2.5">
        <span
          className={`font-semibold ${
            toScore99(score) >= 80
              ? 'text-green-500'
              : toScore99(score) >= 65
                ? 'text-yellow-500'
                : 'text-red-500'
          }`}
        >
          {toScore99(score)}
        </span>
      </td>
    </tr>
  );
}
